/**
 * /promo — landing pública de la promo vigente (contrato sales-hub "promos automáticas").
 *
 * GET /api/promos/active?slug= trae la promo (la crea sales-hub con POST /api/hub/promos) con los
 * planes REALES y su precio con descuento. Sin promo vigente → redirect a la home conservando la
 * query. Los CTA llevan al alta (/register) arrastrando el código (?cupon=) y los UTM; el código
 * además queda en localStorage (utils/pendingCoupon) y el backend lo aplica al suscribirse.
 *
 * Precios: SOLO el precio real del plan vs. el precio con descuento. No se muestran precios
 * "originales" de marketing (originalPrice / "Antes $X").
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Container, Stack, TextField, Typography } from '@mui/material';
import { CheckCircle, ContentCopy } from '@mui/icons-material';
import { apiService } from '../services/api';
import { setPendingCoupon } from '../utils/pendingCoupon';

const CREAM = '#F5EFE0';
const CREAM_SOFT = '#FAF6EC';
const INK = '#0A0A0A';
const INK_SOFT = 'rgba(10,10,10,0.66)';
const YELLOW = '#F6D058';
const YELLOW_DEEP = '#E8B923';
const RULE = 'rgba(10,10,10,0.12)';
const DISPLAY = '"Instrument Serif", "Times New Roman", Georgia, serif';
const SANS = '"Work Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const MONO = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

interface PromoPlan {
  code: string;
  name: string;
  price: number;
  discountedPrice: number;
  currency: string;
}

interface ActivePromo {
  slug: string;
  code: string;
  title: string;
  headline: string;
  subheadline?: string | null;
  bullets: string[];
  description?: string | null;
  discountPercent: number;
  durationMonths: number;
  endsAt: string;
  imageUrl?: string | null;
  imageCredit?: string | null;
  plans: PromoPlan[];
}

interface CouponCheck {
  valid: boolean;
  code?: string;
  discountPercent?: number;
  durationMonths?: number;
  message?: string;
  plans?: PromoPlan[];
}

const money = (n: number, currency: string) =>
  `${currency === 'USD' ? 'US$' : '$'}${Math.round(n).toLocaleString('es-AR')}`;

const monthsLabel = (n: number) => (n === 1 ? 'el primer mes' : `los primeros ${n} meses`);

const ddmm = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', timeZone: 'America/Argentina/Buenos_Aires' });
};

const PromoPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [promo, setPromo] = useState<ActivePromo | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState<CouponCheck | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const api = (apiService as any).api;
        const slug = params.get('slug');
        const res = await api.get('/promos/active', { params: slug ? { slug } : undefined });
        if (!alive) return;
        const data: ActivePromo = res.data;
        setPromo(data);
        setPendingCoupon(params.get('cupon') || data.code);
        setCodeInput((params.get('cupon') || data.code || '').toUpperCase());

        // utm_campaign = slug si no vino en la URL (para medir la promo en analytics).
        if (!params.get('utm_campaign')) {
          const q = new URLSearchParams(window.location.search);
          q.set('utm_campaign', data.slug);
          window.history.replaceState(null, '', `${window.location.pathname}?${q.toString()}`);
        }
      } catch {
        if (!alive) return;
        // Sin promo vigente: a la home conservando la query.
        navigate({ pathname: '/', search: window.location.search }, { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // Solo al montar: el replaceState de utm_campaign no debe volver a disparar la carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signupHref = useMemo(() => {
    if (!promo) return '/register';
    const q = new URLSearchParams(window.location.search);
    const out = new URLSearchParams();
    q.forEach((v, k) => {
      if (k.startsWith('utm_')) out.set(k, v);
    });
    if (!out.get('utm_campaign')) out.set('utm_campaign', promo.slug);
    out.set('cupon', (check?.valid && check.code) || promo.code);
    return `/register?${out.toString()}`;
  }, [promo, check]);

  const copyCode = async () => {
    if (!promo) return;
    try {
      await navigator.clipboard.writeText(promo.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* sin permiso de portapapeles: el código queda a la vista */
    }
  };

  const validate = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const code = codeInput.trim();
    if (!code) return;
    setChecking(true);
    setCheck(null);
    try {
      const api = (apiService as any).api;
      const res = await api.get('/coupons/validate', { params: { code } });
      setCheck(res.data);
      if (res.data?.valid) setPendingCoupon(res.data.code);
    } catch (err: any) {
      setCheck({ valid: false, message: err?.response?.data?.message || 'No pudimos validar el código. Probá de nuevo.' });
    } finally {
      setChecking(false);
    }
  };

  if (loading || !promo) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: INK }} />
      </Box>
    );
  }

  const badge = `${promo.discountPercent}% OFF ${monthsLabel(promo.durationMonths)}`;

  const ctaSx = {
    bgcolor: YELLOW,
    color: INK,
    fontFamily: SANS,
    fontWeight: 700,
    fontSize: 16,
    textTransform: 'none' as const,
    borderRadius: '999px',
    px: 4,
    py: 1.5,
    boxShadow: 'none',
    '&:hover': { bgcolor: YELLOW_DEEP, boxShadow: 'none' },
  };

  const checkPlans = check?.valid && check.plans ? check.plans : null;

  return (
    <Box sx={{ bgcolor: CREAM, color: INK, fontFamily: SANS, minHeight: '100vh', overflowX: 'hidden' }}>
      {/* 1. HERO */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 480, md: 560 },
          display: 'flex',
          alignItems: 'flex-end',
          bgcolor: INK,
          backgroundImage: promo.imageUrl ? `url("${promo.imageUrl}")` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.78) 100%)' }} />
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
          <Container maxWidth="lg" sx={{ py: 2.5, px: { xs: 2, md: 3 } }}>
            <Typography component={RouterLink} to="/" sx={{ fontFamily: DISPLAY, fontSize: 28, color: '#fff', textDecoration: 'none' }}>
              DashFlow<Box component="span" sx={{ color: YELLOW }}>.</Box>
            </Typography>
          </Container>
        </Box>
        <Container maxWidth="lg" sx={{ position: 'relative', py: { xs: 5, md: 8 }, px: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: 'inline-block',
              bgcolor: YELLOW,
              color: INK,
              fontFamily: MONO,
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              px: 1.5,
              py: 0.75,
              borderRadius: '4px',
              mb: 2.5,
            }}
          >
            {badge}
          </Box>
          <Typography
            component="h1"
            sx={{ fontFamily: DISPLAY, fontSize: { xs: 40, md: 64 }, lineHeight: 1.05, color: '#fff', maxWidth: 820, mb: 2 }}
          >
            {promo.headline}
          </Typography>
          {promo.subheadline && (
            <Typography sx={{ fontSize: { xs: 17, md: 20 }, color: 'rgba(255,255,255,0.88)', maxWidth: 680, mb: 4 }}>
              {promo.subheadline}
            </Typography>
          )}
          <Button component={RouterLink} to={signupHref} variant="contained" sx={ctaSx}>
            Quiero la promo
          </Button>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 }, px: { xs: 2, md: 3 } }}>
        {/* 2. CÓDIGO */}
        <Box sx={{ bgcolor: '#fff', border: `1px solid ${RULE}`, borderRadius: '14px', p: { xs: 3, md: 4 }, mb: 6 }}>
          <Typography sx={{ fontFamily: MONO, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: INK_SOFT, mb: 1.5 }}>
            Tu código
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 3 }}>
            <Box
              sx={{
                flex: 1,
                border: `2px dashed ${INK}`,
                borderRadius: '10px',
                py: 2,
                textAlign: 'center',
                fontFamily: MONO,
                fontSize: { xs: 28, md: 36 },
                fontWeight: 600,
                letterSpacing: '0.12em',
                bgcolor: CREAM_SOFT,
                wordBreak: 'break-all',
              }}
            >
              {promo.code}
            </Box>
            <Button
              onClick={copyCode}
              startIcon={copied ? <CheckCircle /> : <ContentCopy />}
              sx={{ ...ctaSx, bgcolor: INK, color: '#fff', '&:hover': { bgcolor: '#222', boxShadow: 'none' } }}
            >
              {copied ? 'Copiado' : 'Copiar'}
            </Button>
          </Stack>

          <Box component="form" onSubmit={validate}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Validá tu código</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                placeholder="CÓDIGO"
                size="small"
                fullWidth
                inputProps={{ maxLength: 50, style: { fontFamily: MONO, letterSpacing: '0.08em' } }}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: '8px' } }}
              />
              <Button type="submit" disabled={checking} variant="outlined" sx={{ textTransform: 'none', fontWeight: 600, color: INK, borderColor: INK, borderRadius: '8px', minWidth: 120 }}>
                {checking ? <CircularProgress size={20} sx={{ color: INK }} /> : 'Validar'}
              </Button>
            </Stack>
            {check && !check.valid && (
              <Alert severity="warning" sx={{ mt: 2 }}>{check.message || 'Ese código no es válido.'}</Alert>
            )}
            {checkPlans && (
              <Box sx={{ mt: 2.5 }}>
                <Alert severity="success" sx={{ mb: 1.5 }}>
                  Código válido: {check!.discountPercent}% OFF {monthsLabel(check!.durationMonths || 1)}.
                </Alert>
                {checkPlans.map((p) => (
                  <Stack key={p.code} direction="row" justifyContent="space-between" sx={{ py: 1, borderBottom: `1px solid ${RULE}` }}>
                    <Typography>{p.name}</Typography>
                    <Typography>
                      <Box component="span" sx={{ textDecoration: 'line-through', color: INK_SOFT, mr: 1 }}>{money(p.price, p.currency)}</Box>
                      <Box component="span" sx={{ fontWeight: 700 }}>{money(p.discountedPrice, p.currency)}</Box>
                    </Typography>
                  </Stack>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {/* 3. BENEFICIOS */}
        {promo.bullets.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography component="h2" sx={{ fontFamily: DISPLAY, fontSize: { xs: 32, md: 40 }, mb: 2.5 }}>
              Qué te llevás
            </Typography>
            <Stack spacing={1.75}>
              {promo.bullets.map((b, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                  <CheckCircle sx={{ color: YELLOW_DEEP, mt: '2px' }} />
                  <Typography sx={{ fontSize: 17 }}>{b}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        {/* 4. PLANES */}
        {promo.plans.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography component="h2" sx={{ fontFamily: DISPLAY, fontSize: { xs: 32, md: 40 }, mb: 2.5 }}>
              Planes con la promo
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${Math.min(promo.plans.length, 3)}, 1fr)` }, gap: 2 }}>
              {promo.plans.map((p) => (
                <Box key={p.code} sx={{ bgcolor: '#fff', border: `1px solid ${RULE}`, borderRadius: '14px', p: 3, display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1.5 }}>{p.name}</Typography>
                  <Typography sx={{ textDecoration: 'line-through', color: INK_SOFT, fontSize: 16 }}>
                    {money(p.price, p.currency)}/mes
                  </Typography>
                  <Typography sx={{ fontFamily: DISPLAY, fontSize: 40, lineHeight: 1.1 }}>
                    {money(p.discountedPrice, p.currency)}
                    <Box component="span" sx={{ fontFamily: SANS, fontSize: 15, color: INK_SOFT }}>/mes</Box>
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: INK_SOFT, mt: 1, mb: 2.5, flex: 1 }}>
                    Por {promo.durationMonths === 1 ? '1 mes' : `${promo.durationMonths} meses`}, después {money(p.price, p.currency)}/mes.
                  </Typography>
                  <Button component={RouterLink} to={signupHref} variant="contained" sx={{ ...ctaSx, px: 2 }}>
                    Quiero la promo
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* 5. CONDICIONES */}
        <Box sx={{ borderTop: `1px solid ${RULE}`, pt: 3, mb: 6 }}>
          <Typography component="h2" sx={{ fontWeight: 700, fontSize: 18, mb: 1.5 }}>Condiciones</Typography>
          <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, m: 0, color: INK_SOFT, fontSize: 15 }}>
            <li>Válido si te suscribís hasta el {ddmm(promo.endsAt)}. Un cupón por cuenta.</li>
            <li>
              {promo.discountPercent}% de descuento en {promo.durationMonths === 1 ? 'tu primer pago mensual' : `tus primeros ${promo.durationMonths} pagos mensuales`}; después se cobra el precio normal del plan.
            </li>
            <li>El código se aplica solo cuando te suscribís con Mercado Pago.</li>
            <li>Cancelás cuando quieras.</li>
          </Stack>
          {promo.description && (
            <Typography sx={{ mt: 2, fontSize: 14, color: INK_SOFT }}>{promo.description}</Typography>
          )}
        </Box>

        {/* 6. CTA FINAL */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: { xs: 30, md: 38 }, mb: 2.5 }}>{badge}</Typography>
          <Button component={RouterLink} to={signupHref} variant="contained" sx={ctaSx}>
            Quiero la promo
          </Button>
        </Box>

        {promo.imageCredit && (
          <Typography sx={{ textAlign: 'center', fontSize: 11, color: INK_SOFT, fontFamily: MONO }}>{promo.imageCredit}</Typography>
        )}
      </Container>
    </Box>
  );
};

export default PromoPage;
